import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Search, Eye, Download, Phone, Mail, MapPin, Package, Truck,
  CreditCard, Clock, RotateCcw, AlertTriangle, Hash, CheckCircle, XCircle, Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-blue-100 text-blue-800",
  paid: "bg-emerald-100 text-emerald-800",
  processing: "bg-sky-100 text-sky-800",
  shipped: "bg-purple-100 text-purple-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
  refunded: "bg-gray-200 text-gray-700",
  captured: "bg-emerald-100 text-emerald-800",
  failed: "bg-red-100 text-red-800",
  authorized: "bg-blue-100 text-blue-800",
};

const AdminOrdersPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundLoading, setRefundLoading] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingCarrier, setTrackingCarrier] = useState("UPS");
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [shipmentLoading, setShipmentLoading] = useState(false);
  const [shipmentServiceCode, setShipmentServiceCode] = useState("03");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelVoidShipment, setCancelVoidShipment] = useState(true);
  const [cancelRefund, setCancelRefund] = useState(true);
  const [cancelResult, setCancelResult] = useState(null);

  useEffect(() => {
    if (user && user.role !== "admin") navigate("/");
  }, [user, navigate]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/orders?limit=200`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setOrders(Array.isArray(response.data) ? response.data : response.data?.orders || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status }, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast.success(`Order status updated to ${status}`);
      fetchOrders();
      if (selectedOrder?.order_id === orderId) {
        setSelectedOrder((prev) => ({ ...prev, status }));
      }
    } catch (error) {
      toast.error("Failed to update order status");
    }
  };

  const processRefund = async () => {
    if (!selectedOrder?.payment_transaction_id) {
      toast.error("No NMI transaction ID on this order — cannot process refund");
      return;
    }
    setRefundLoading(true);
    try {
      const payload = {};
      if (refundAmount && parseFloat(refundAmount) > 0) {
        payload.amount = parseFloat(refundAmount);
      }
      const res = await axios.post(`${API}/admin/orders/${selectedOrder.order_id}/refund`, payload, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast.success(res.data?.message || "Refund processed successfully");
      setRefundDialogOpen(false);
      setRefundAmount("");
      fetchOrders();
      if (res.data?.order) setSelectedOrder(res.data.order);
    } catch (error) {
      const msg = error.response?.data?.detail || "Refund failed";
      toast.error(msg);
    } finally {
      setRefundLoading(false);
    }
  };

  const addTracking = async () => {
    if (!trackingNumber.trim()) {
      toast.error("Please enter a tracking number");
      return;
    }
    setTrackingLoading(true);
    try {
      const res = await axios.put(`${API}/admin/orders/${selectedOrder.order_id}/tracking`, {
        tracking_number: trackingNumber.trim(),
        carrier: trackingCarrier,
      }, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast.success("Tracking added — shipment notification email sent");
      setTrackingNumber("");
      fetchOrders();
      if (res.data?.order) setSelectedOrder(res.data.order);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add tracking");
    } finally {
      setTrackingLoading(false);
    }
  };

  const createShipment = async () => {
    if (shipmentLoading) return;
    setShipmentLoading(true);
    try {
      const res = await axios.post(`${API}/admin/orders/${selectedOrder.order_id}/ship`, {
        service_code: shipmentServiceCode,
      }, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.data?.already_exists) {
        toast.info(`Shipment already exists. Tracking: ${res.data.tracking_number}`);
      } else if (res.data?.db_persist_failed) {
        toast.warning(`Shipment created (Tracking: ${res.data.tracking_number}) but DB update failed. Please add tracking manually.`);
      } else {
        toast.success(`Shipment created! Tracking: ${res.data.tracking_number}`);
      }
      fetchOrders();
      if (res.data?.order) setSelectedOrder(res.data.order);
    } catch (error) {
      const detail = error.response?.data?.detail || "Shipment creation failed";
      console.error("Shipment creation error:", error.response?.status, detail);
      toast.error(detail);
    } finally {
      setShipmentLoading(false);
    }
  };

  const openCancelDialog = () => {
    setCancelResult(null);
    setCancelReason("");
    setCancelVoidShipment(true);
    setCancelRefund(true);
    setCancelDialogOpen(true);
  };

  const processCancellation = async () => {
    if (cancelLoading) return;
    setCancelLoading(true);
    setCancelResult(null);
    try {
      const res = await axios.post(`${API}/admin/orders/${selectedOrder.order_id}/cancel`, {
        reason: cancelReason || undefined,
        void_shipment: cancelVoidShipment,
        refund_payment: cancelRefund,
      }, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setCancelResult(res.data);
      if (res.data?.already_cancelled) {
        toast.info("Order is already cancelled.");
      } else {
        toast.success("Order cancellation completed.");
      }
      fetchOrders();
      if (res.data?.order) setSelectedOrder(res.data.order);
    } catch (error) {
      const detail = error.response?.data?.detail || "Cancellation failed";
      toast.error(detail);
      setCancelResult({ success: false, message: detail });
    } finally {
      setCancelLoading(false);
    }
  };

  const filteredOrders = orders.filter((order) => {
    if (statusFilter !== "all" && order.status !== statusFilter && order.payment_status !== statusFilter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      const fields = [
        order.order_number, order.order_id, order.customer_name, order.customer_email,
        order.customer_phone, order.payment_transaction_id,
        order.shipping_address?.name, order.shipping_address?.city,
      ];
      if (!fields.some((f) => f && String(f).toLowerCase().includes(s))) return false;
    }
    if (dateRange.start && order.created_at) {
      if (new Date(order.created_at) < new Date(dateRange.start)) return false;
    }
    if (dateRange.end && order.created_at) {
      const end = new Date(dateRange.end);
      end.setHours(23, 59, 59);
      if (new Date(order.created_at) > end) return false;
    }
    return true;
  });

  const openOrderDetails = (order) => { setSelectedOrder(order); setDetailsOpen(true); };

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  const exportToCSV = () => {
    const headers = ["Order Number", "Date", "Customer", "Email", "Phone", "City/State", "Subtotal", "Tax", "Shipping", "Total", "Status", "Payment Status", "NMI TXN ID"];
    const rows = filteredOrders.map((o) => [
      o.order_number || o.order_id,
      fmt(o.created_at),
      o.customer_name || "",
      o.customer_email || "",
      o.customer_phone || "",
      `${o.shipping_address?.city || ""} ${o.shipping_address?.state || ""}`.trim(),
      (o.subtotal || 0).toFixed(2),
      (o.tax_amount || 0).toFixed(2),
      (o.shipping_amount || 0).toFixed(2),
      (o.total || 0).toFixed(2),
      o.status,
      o.payment_status,
      o.payment_transaction_id || "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    toast.success("Orders exported");
  };

  const statusTabs = [
    { value: "all", label: "All", count: orders.length },
    { value: "pending", label: "Pending", count: orders.filter((o) => o.status === "pending").length },
    { value: "paid", label: "Paid", count: orders.filter((o) => o.status === "paid" || o.payment_status === "captured").length },
    { value: "processing", label: "Processing", count: orders.filter((o) => o.status === "processing").length },
    { value: "shipped", label: "Shipped", count: orders.filter((o) => o.status === "shipped").length },
    { value: "delivered", label: "Delivered", count: orders.filter((o) => o.status === "delivered").length },
    { value: "cancelled", label: "Cancelled", count: orders.filter((o) => o.status === "cancelled").length },
    { value: "refunded", label: "Refunded", count: orders.filter((o) => o.status === "refunded").length },
  ];

  const Badge = ({ status }) => (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${statusColors[status] || "bg-gray-100 text-gray-600"}`} data-testid={`badge-${status}`}>
      {status}
    </span>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-orders-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-6">
        <div className="container mx-auto px-4">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-primary mb-4" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 uppercase tracking-tight">
              Orders Management
            </h1>
            <Button onClick={exportToCSV} variant="outline" data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm text-gray-600 mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Order #, name, email, transaction ID..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="order-search" />
              </div>
            </div>
            <div className="w-40">
              <label className="text-sm text-gray-600 mb-1 block">From</label>
              <Input type="date" value={dateRange.start} onChange={(e) => setDateRange((p) => ({ ...p, start: e.target.value }))} data-testid="date-from" />
            </div>
            <div className="w-40">
              <label className="text-sm text-gray-600 mb-1 block">To</label>
              <Input type="date" value={dateRange.end} onChange={(e) => setDateRange((p) => ({ ...p, end: e.target.value }))} data-testid="date-to" />
            </div>
            <Button variant="outline" onClick={() => { setSearchTerm(""); setDateRange({ start: "", end: "" }); setStatusFilter("all"); }} data-testid="clear-filters-btn">
              Clear
            </Button>
          </div>
        </div>

        {/* Status Tabs */}
        <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-full">
          <TabsList className="bg-white border border-gray-200 p-1 mb-4 flex-wrap h-auto gap-1">
            {statusTabs.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid={`tab-${tab.value}`}>
                {tab.label}
                <span className="ml-1.5 px-1.5 py-0.5 bg-gray-100 data-[state=active]:bg-white/20 text-xs rounded">{tab.count}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Orders Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
              <p className="mt-2 text-gray-500">Loading orders...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No orders found</p>
              <p className="text-sm mt-1">Orders will appear here after customers complete purchases.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="orders-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left p-3 font-medium text-gray-600 text-xs uppercase tracking-wider">Order #</th>
                    <th className="text-left p-3 font-medium text-gray-600 text-xs uppercase tracking-wider">Date</th>
                    <th className="text-left p-3 font-medium text-gray-600 text-xs uppercase tracking-wider">Customer</th>
                    <th className="text-right p-3 font-medium text-gray-600 text-xs uppercase tracking-wider">Total</th>
                    <th className="text-center p-3 font-medium text-gray-600 text-xs uppercase tracking-wider">Status</th>
                    <th className="text-center p-3 font-medium text-gray-600 text-xs uppercase tracking-wider">Payment</th>
                    <th className="text-left p-3 font-medium text-gray-600 text-xs uppercase tracking-wider">NMI TXN</th>
                    <th className="text-center p-3 font-medium text-gray-600 text-xs uppercase tracking-wider">View</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr key={order.order_id} className="border-b border-gray-100 hover:bg-gray-50/70 cursor-pointer transition-colors" onClick={() => openOrderDetails(order)} data-testid={`order-row-${order.order_id}`}>
                      <td className="p-3">
                        <span className="font-mono text-sm font-semibold text-primary" data-testid={`order-number-${order.order_id}`}>{order.order_number || order.order_id?.slice(0, 12)}</span>
                      </td>
                      <td className="p-3 text-sm text-gray-600 whitespace-nowrap">{fmt(order.created_at)}</td>
                      <td className="p-3">
                        <p className="font-medium text-gray-900 text-sm">{order.customer_name || "—"}</p>
                        <p className="text-xs text-gray-500">{order.customer_email || ""}</p>
                      </td>
                      <td className="p-3 text-right font-bold text-gray-900">${(order.total || 0).toFixed(2)}</td>
                      <td className="p-3 text-center"><Badge status={order.status} /></td>
                      <td className="p-3 text-center"><Badge status={order.payment_status} /></td>
                      <td className="p-3">
                        {order.payment_transaction_id ? (
                          <span className="font-mono text-xs text-gray-700 bg-gray-100 px-2 py-0.5 rounded" data-testid={`txn-id-${order.order_id}`}>{order.payment_transaction_id}</span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" onClick={() => openOrderDetails(order)} data-testid={`view-order-${order.order_id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Order Details Dialog ── */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Package className="w-5 h-5" />
              Order {selectedOrder?.order_number || selectedOrder?.order_id?.slice(0, 12)}
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6" data-testid="order-detail-content">
              {/* Status Bar */}
              <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Badge status={selectedOrder.status} />
                  <span className="text-xs text-gray-400">|</span>
                  <Badge status={selectedOrder.payment_status} />
                </div>
                <div className="flex flex-wrap gap-2">
                  {["processing", "shipped", "delivered", "cancelled"].map((s) => (
                    <Button key={s} size="sm" variant={selectedOrder.status === s ? "default" : "outline"} onClick={() => updateOrderStatus(selectedOrder.order_id, s)} className="capitalize text-xs" data-testid={`status-btn-${s}`}>
                      {s}
                    </Button>
                  ))}
                </div>
              </div>

              {/* NMI Transaction & Payment Info */}
              <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg" data-testid="payment-info-section">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3 text-sm uppercase tracking-wide">
                  <CreditCard className="w-4 h-4" /> Payment Information
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs">NMI Transaction ID</p>
                    <p className="font-mono font-bold text-gray-900" data-testid="detail-txn-id">{selectedOrder.payment_transaction_id || "—"}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Authorization Code</p>
                    <p className="font-mono font-bold text-gray-900" data-testid="detail-auth-code">{selectedOrder.payment_authorization_code || "—"}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Payment Method</p>
                    <p className="font-semibold text-gray-900">{selectedOrder.payment_method?.toUpperCase() || "—"}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Card</p>
                    <p className="font-semibold text-gray-900">
                      {selectedOrder.payment_card_type ? `${selectedOrder.payment_card_type} ****${selectedOrder.payment_last_four}` : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Payment Status</p>
                    <Badge status={selectedOrder.payment_status} />
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Paid At</p>
                    <p className="text-gray-900">{fmt(selectedOrder.paid_at)}</p>
                  </div>
                </div>

                {/* Refund info if refunded */}
                {selectedOrder.refund_transaction_id && (
                  <div className="mt-4 pt-3 border-t border-amber-200">
                    <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">Refund Record</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500 text-xs">Refund TXN ID</p>
                        <p className="font-mono font-bold text-red-700" data-testid="refund-txn-id">{selectedOrder.refund_transaction_id}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Refund Amount</p>
                        <p className="font-bold text-red-700">${selectedOrder.refund_amount?.toFixed(2) || "—"}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Refunded By</p>
                        <p className="text-gray-900">{selectedOrder.refunded_by || "—"}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Refund Button */}
                {selectedOrder.payment_transaction_id && selectedOrder.payment_status !== "refunded" && (
                  <div className="mt-4 pt-3 border-t border-amber-200">
                    <Button variant="destructive" size="sm" onClick={() => { setRefundAmount(""); setRefundDialogOpen(true); }} data-testid="refund-order-btn">
                      <RotateCcw className="w-4 h-4 mr-2" /> Process Refund
                    </Button>
                  </div>
                )}
              </div>

              {/* Customer + Shipping + Totals */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Customer & Shipping */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-sm uppercase tracking-wide">
                    <MapPin className="w-4 h-4" /> Customer & Shipping
                  </h3>
                  <div className="p-4 border rounded-lg space-y-2 text-sm bg-white">
                    <p className="font-semibold text-gray-900">{selectedOrder.customer_name || selectedOrder.shipping_address?.name || "—"}</p>
                    {selectedOrder.customer_email && (
                      <p className="flex items-center gap-2 text-gray-600"><Mail className="w-3 h-3" /> {selectedOrder.customer_email}</p>
                    )}
                    {selectedOrder.customer_phone && (
                      <p className="flex items-center gap-2 text-gray-600"><Phone className="w-3 h-3" /> {selectedOrder.customer_phone}</p>
                    )}
                    <hr className="my-2" />
                    <p>{selectedOrder.shipping_address?.line1}</p>
                    {selectedOrder.shipping_address?.line2 && <p>{selectedOrder.shipping_address.line2}</p>}
                    <p>{selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.state} {selectedOrder.shipping_address?.postal_code}</p>
                    {selectedOrder.shipping_carrier && (
                      <p className="flex items-center gap-2 text-gray-600 pt-1"><Truck className="w-3 h-3" /> {selectedOrder.shipping_carrier} — {selectedOrder.shipping_method}</p>
                    )}
                    {selectedOrder.tracking_number && (
                      <p className="flex items-center gap-2 text-gray-600"><Hash className="w-3 h-3" /> Tracking: <span className="font-mono">{selectedOrder.tracking_number}</span></p>
                    )}
                    {selectedOrder.is_guest && <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">Guest Order</span>}
                  </div>
                </div>

                {/* Order Totals */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-sm uppercase tracking-wide">
                    <CreditCard className="w-4 h-4" /> Order Totals
                  </h3>
                  <div className="p-4 border rounded-lg space-y-2 text-sm bg-white">
                    <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span>${(selectedOrder.subtotal || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Shipping</span><span>${(selectedOrder.shipping_amount || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Tax</span><span>${(selectedOrder.tax_amount || 0).toFixed(2)}</span></div>
                    {selectedOrder.discount_amount > 0 && (
                      <div className="flex justify-between"><span className="text-gray-600">Discount</span><span className="text-red-600">-${(selectedOrder.discount_amount).toFixed(2)}</span></div>
                    )}
                    <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                      <span>Total</span>
                      <span data-testid="order-total">${(selectedOrder.total || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-500 pt-2 text-xs">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Order Date</span>
                      <span>{fmt(selectedOrder.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3 text-sm uppercase tracking-wide">
                  <Package className="w-4 h-4" /> Items ({selectedOrder.items?.length || 0})
                </h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full" data-testid="order-items-table">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3 text-xs font-medium text-gray-600 uppercase">Product</th>
                        <th className="text-center p-3 text-xs font-medium text-gray-600 uppercase">SKU</th>
                        <th className="text-center p-3 text-xs font-medium text-gray-600 uppercase">Qty</th>
                        <th className="text-right p-3 text-xs font-medium text-gray-600 uppercase">Unit Price</th>
                        <th className="text-right p-3 text-xs font-medium text-gray-600 uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items?.map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              {item.product_image && <img src={item.product_image} alt="" className="w-10 h-10 rounded object-cover border" />}
                              <span className="font-medium text-gray-900 text-sm">{item.product_name || item.name || "Product"}</span>
                            </div>
                          </td>
                          <td className="p-3 text-center text-xs text-gray-500 font-mono">{item.product_sku || "—"}</td>
                          <td className="p-3 text-center font-medium">{item.quantity}</td>
                          <td className="p-3 text-right text-sm">${(item.unit_price || item.price || 0).toFixed(2)}</td>
                          <td className="p-3 text-right font-semibold text-sm">${(item.total || ((item.unit_price || item.price || 0) * (item.quantity || 1))).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── Shipment Section ── */}
              <div className="p-4 border border-gray-200 rounded-lg" data-testid="shipment-section">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3 text-sm uppercase tracking-wide">
                  <Truck className="w-4 h-4" /> Shipment & Fulfillment
                </h3>

                {/* Already Shipped */}
                {selectedOrder.tracking_number ? (
                  <div className="space-y-3">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="font-semibold text-green-800 text-sm">Shipped</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-gray-500 text-xs">Carrier</p>
                          <p className="font-semibold text-gray-900">{selectedOrder.shipping_carrier || "UPS"}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">Service</p>
                          <p className="font-semibold text-gray-900">{selectedOrder.shipment_service || selectedOrder.shipping_method || "—"}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">Tracking Number</p>
                          <p className="font-mono font-bold text-gray-900" data-testid="tracking-number">{selectedOrder.tracking_number}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">Shipped At</p>
                          <p className="text-gray-900">{fmt(selectedOrder.shipped_at)}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-green-200">
                        {selectedOrder.shipping_carrier === "UPS" && (
                          <a
                            href={`https://www.ups.com/track?tracknum=${selectedOrder.tracking_number}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-blue-700 hover:underline font-medium"
                            data-testid="track-ups-link"
                          >
                            <Truck className="w-3.5 h-3.5" /> Track on UPS.com
                          </a>
                        )}
                        {selectedOrder.has_label && (
                          <a
                            href={`${API}/admin/orders/${selectedOrder.order_id}/label`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm text-purple-700 hover:underline font-medium"
                            data-testid="download-label-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              axios.get(`${API}/admin/orders/${selectedOrder.order_id}/label`, {
                                responseType: 'blob',
                                withCredentials: true,
                                headers: token ? { Authorization: `Bearer ${token}` } : {},
                              }).then((res) => {
                                const url = URL.createObjectURL(res.data);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `label_${selectedOrder.order_number || selectedOrder.order_id.slice(0,12)}.pdf`;
                                a.click();
                                URL.revokeObjectURL(url);
                                toast.success("Label downloaded");
                              }).catch(() => toast.error("Label download failed"));
                            }}
                          >
                            <Download className="w-3.5 h-3.5" /> Download Label
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Not Yet Shipped — Show Create Shipment or Manual Tracking */
                  <>
                    {/* Eligibility check */}
                    {(selectedOrder.payment_status === "captured" || selectedOrder.status === "paid") &&
                     selectedOrder.status !== "cancelled" && selectedOrder.status !== "refunded" ? (
                      <div className="space-y-4">
                        {/* UPS Auto Shipment */}
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="font-semibold text-blue-800 text-sm mb-3">Create UPS Shipment</p>
                          <div className="flex items-center gap-3 mb-3">
                            <select
                              value={shipmentServiceCode}
                              onChange={(e) => setShipmentServiceCode(e.target.value)}
                              className="px-3 py-2 border border-blue-200 rounded-md text-sm bg-white flex-1"
                              data-testid="shipment-service-select"
                            >
                              <option value="03">UPS Ground (5-7 days)</option>
                              <option value="12">UPS 3 Day Select</option>
                              <option value="02">UPS 2nd Day Air</option>
                              <option value="13">UPS Next Day Air Saver</option>
                              <option value="01">UPS Next Day Air</option>
                            </select>
                          </div>
                          <Button
                            onClick={createShipment}
                            disabled={shipmentLoading}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid="create-shipment-btn"
                          >
                            {shipmentLoading ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating Shipment...</>
                            ) : (
                              <><Truck className="w-4 h-4 mr-2" /> Create Shipment & Generate Label</>
                            )}
                          </Button>
                          <p className="text-xs text-blue-600 mt-2">
                            This will create a UPS shipment, generate a shipping label, and send a tracking email to the customer.
                          </p>
                        </div>

                        {/* Manual Tracking Fallback */}
                        <details className="text-sm">
                          <summary className="cursor-pointer text-gray-500 hover:text-gray-700 font-medium">
                            Or add tracking manually...
                          </summary>
                          <div className="mt-3 space-y-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex gap-2">
                              <Input
                                placeholder="Enter tracking number..."
                                value={trackingNumber}
                                onChange={(e) => setTrackingNumber(e.target.value)}
                                className="flex-1"
                                data-testid="tracking-input"
                              />
                              <select
                                value={trackingCarrier}
                                onChange={(e) => setTrackingCarrier(e.target.value)}
                                className="px-3 py-2 border border-gray-200 rounded-md text-sm"
                                data-testid="tracking-carrier-select"
                              >
                                <option value="UPS">UPS</option>
                                <option value="USPS">USPS</option>
                                <option value="FedEx">FedEx</option>
                              </select>
                            </div>
                            <Button size="sm" onClick={addTracking} disabled={trackingLoading} data-testid="add-tracking-btn">
                              {trackingLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Truck className="w-4 h-4 mr-2" />}
                              {trackingLoading ? "Saving..." : "Add Tracking & Notify Customer"}
                            </Button>
                          </div>
                        </details>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 py-2">
                        {selectedOrder.status === "cancelled" || selectedOrder.status === "refunded"
                          ? `Shipment not available for ${selectedOrder.status} orders.`
                          : "Shipment available after payment is confirmed."}
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* ── Cancel Order Section ── */}
              {selectedOrder.status !== "cancelled" && selectedOrder.status !== "delivered" && (
                <div className="p-4 border border-red-200 rounded-lg bg-red-50/50" data-testid="cancel-order-section">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3 text-sm uppercase tracking-wide">
                    <XCircle className="w-4 h-4 text-red-500" /> Cancel Order
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Cancel this order{selectedOrder.tracking_number ? ", void the UPS shipment/label," : ""} and refund the customer.
                  </p>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={openCancelDialog}
                    data-testid="cancel-order-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" /> Cancel Order
                  </Button>
                </div>
              )}

              {/* Cancelled order info */}
              {selectedOrder.status === "cancelled" && (
                <div className="p-4 border border-red-300 rounded-lg bg-red-50" data-testid="cancelled-info-section">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="w-4 h-4 text-red-600" />
                    <span className="font-semibold text-red-800 text-sm">Order Cancelled</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-500 text-xs">Cancelled At</p>
                      <p className="text-gray-900">{fmt(selectedOrder.cancelled_at)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Cancelled By</p>
                      <p className="text-gray-900">{selectedOrder.cancelled_by || "—"}</p>
                    </div>
                    {selectedOrder.cancellation_reason && (
                      <div className="col-span-2">
                        <p className="text-gray-500 text-xs">Reason</p>
                        <p className="text-gray-900">{selectedOrder.cancellation_reason}</p>
                      </div>
                    )}
                    {selectedOrder.void_status && (
                      <div>
                        <p className="text-gray-500 text-xs">UPS Void Status</p>
                        <p className={`font-semibold ${selectedOrder.void_status === "success" ? "text-green-700" : selectedOrder.void_status === "failed" ? "text-red-700" : "text-gray-600"}`}>
                          {selectedOrder.void_status === "success" ? "Voided" : selectedOrder.void_status === "failed" ? "Void Failed" : selectedOrder.void_status === "not_applicable" ? "N/A" : selectedOrder.void_status}
                        </p>
                      </div>
                    )}
                    {selectedOrder.voided_at && (
                      <div>
                        <p className="text-gray-500 text-xs">Voided At</p>
                        <p className="text-gray-900">{fmt(selectedOrder.voided_at)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Admin Notes */}
              {selectedOrder.admin_notes && (
                <div className="p-4 border rounded-lg bg-gray-50">
                  <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wide mb-2">Admin Notes</h3>
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">{selectedOrder.admin_notes}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Refund Confirmation Dialog ── */}
      <Dialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Process Refund
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="refund-dialog-content">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
              <p className="font-semibold text-red-800">This will issue a refund through NMI.</p>
              <p className="text-red-700 mt-1">
                Order: <span className="font-mono font-bold">{selectedOrder?.order_number}</span>
              </p>
              <p className="text-red-700">
                NMI TXN: <span className="font-mono font-bold">{selectedOrder?.payment_transaction_id}</span>
              </p>
              <p className="text-red-700">
                Order Total: <span className="font-bold">${selectedOrder?.total?.toFixed(2)}</span>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Refund Amount (leave blank for full refund)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                max={selectedOrder?.total}
                placeholder={`Full refund: $${selectedOrder?.total?.toFixed(2) || "0.00"}`}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                data-testid="refund-amount-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRefundDialogOpen(false)} data-testid="refund-cancel-btn">Cancel</Button>
            <Button variant="destructive" onClick={processRefund} disabled={refundLoading} data-testid="refund-confirm-btn">
              {refundLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              {refundLoading ? "Processing..." : `Refund $${refundAmount || selectedOrder?.total?.toFixed(2) || "0.00"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Cancel Order Confirmation Dialog ── */}
      <Dialog open={cancelDialogOpen} onOpenChange={(open) => { if (!cancelLoading) setCancelDialogOpen(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Cancel Order
            </DialogTitle>
          </DialogHeader>

          {cancelResult ? (
            /* ── Post-action result panel ── */
            <div className="space-y-4" data-testid="cancel-result-panel">
              <div className={`p-4 rounded-lg border ${cancelResult.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                <p className={`font-semibold text-sm ${cancelResult.success ? "text-green-800" : "text-red-800"}`}>
                  {cancelResult.success ? "Cancellation completed successfully." : cancelResult.message || "Cancellation failed."}
                </p>
              </div>
              {cancelResult.actions && (
                <div className="space-y-2 text-sm">
                  {cancelResult.actions.audit_trail?.map((note, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 bg-gray-50 rounded text-xs font-mono">
                      <span className={note.includes("SUCCESS") ? "text-green-600" : note.includes("FAILED") || note.includes("ERROR") ? "text-red-600" : "text-gray-600"}>
                        {note.includes("SUCCESS") ? "OK" : note.includes("FAILED") || note.includes("ERROR") ? "!!" : "--"}
                      </span>
                      <span className="text-gray-700">{note}</span>
                    </div>
                  ))}
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setCancelDialogOpen(false)} data-testid="cancel-close-btn">Close</Button>
              </DialogFooter>
            </div>
          ) : (
            /* ── Pre-action review panel ── */
            <div className="space-y-4" data-testid="cancel-review-panel">
              {/* Order summary */}
              <div className="p-3 bg-gray-50 border rounded-lg text-sm space-y-1">
                <p><span className="text-gray-500">Order:</span> <span className="font-mono font-bold">{selectedOrder?.order_number}</span></p>
                <p><span className="text-gray-500">Status:</span> <Badge status={selectedOrder?.status} /></p>
                <p><span className="text-gray-500">Total:</span> <span className="font-bold">${selectedOrder?.total?.toFixed(2)}</span></p>
              </div>

              {/* Payment status */}
              <div className="p-3 border rounded-lg text-sm space-y-1">
                <p className="font-semibold text-gray-900 text-xs uppercase tracking-wide mb-1">Payment</p>
                <p><span className="text-gray-500">NMI TXN:</span> <span className="font-mono">{selectedOrder?.payment_transaction_id || "None"}</span></p>
                <p><span className="text-gray-500">Status:</span> <Badge status={selectedOrder?.payment_status} /></p>
                {selectedOrder?.payment_status === "refunded" && (
                  <p className="text-yellow-700 text-xs font-semibold">Already refunded — no additional refund will be issued.</p>
                )}
              </div>

              {/* Shipment status */}
              <div className="p-3 border rounded-lg text-sm space-y-1">
                <p className="font-semibold text-gray-900 text-xs uppercase tracking-wide mb-1">Shipment</p>
                {selectedOrder?.tracking_number ? (
                  <>
                    <p><span className="text-gray-500">Tracking:</span> <span className="font-mono font-bold">{selectedOrder.tracking_number}</span></p>
                    <p><span className="text-gray-500">Carrier:</span> {selectedOrder.shipping_carrier || "UPS"}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="checkbox"
                        id="void-shipment-check"
                        checked={cancelVoidShipment}
                        onChange={(e) => setCancelVoidShipment(e.target.checked)}
                        className="rounded border-gray-300"
                        data-testid="cancel-void-checkbox"
                      />
                      <label htmlFor="void-shipment-check" className="text-sm font-medium text-red-700">
                        Void UPS shipment to avoid shipping charge
                      </label>
                    </div>
                    {selectedOrder.status === "shipped" && (
                      <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Warning: If the package is already in transit, UPS void may not be possible.
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-gray-500">No shipment created — no UPS void needed.</p>
                )}
              </div>

              {/* Refund option */}
              <div className="p-3 border rounded-lg text-sm space-y-1">
                <p className="font-semibold text-gray-900 text-xs uppercase tracking-wide mb-1">Refund</p>
                {selectedOrder?.payment_transaction_id && selectedOrder?.payment_status !== "refunded" ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="refund-check"
                      checked={cancelRefund}
                      onChange={(e) => setCancelRefund(e.target.checked)}
                      className="rounded border-gray-300"
                      data-testid="cancel-refund-checkbox"
                    />
                    <label htmlFor="refund-check" className="text-sm font-medium text-gray-900">
                      Refund customer: <span className="font-bold">${selectedOrder?.total?.toFixed(2)}</span>
                    </label>
                  </div>
                ) : selectedOrder?.payment_status === "refunded" ? (
                  <p className="text-gray-500">Payment already refunded.</p>
                ) : (
                  <p className="text-gray-500">No payment transaction found.</p>
                )}
              </div>

              {/* Reason */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Cancellation Reason (optional)</label>
                <Input
                  placeholder="e.g., Customer requested cancellation..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  data-testid="cancel-reason-input"
                />
              </div>

              {/* Warning */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800 font-semibold flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> This action cannot be undone.
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  {cancelVoidShipment && selectedOrder?.tracking_number ? "UPS shipment will be voided. " : ""}
                  {cancelRefund && selectedOrder?.payment_transaction_id && selectedOrder?.payment_status !== "refunded" ? "Customer will be refunded via NMI. " : ""}
                  Order status will be set to cancelled.
                </p>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setCancelDialogOpen(false)} data-testid="cancel-dialog-dismiss-btn">
                  Go Back
                </Button>
                <Button
                  variant="destructive"
                  onClick={processCancellation}
                  disabled={cancelLoading}
                  data-testid="cancel-confirm-btn"
                >
                  {cancelLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                  ) : (
                    <><XCircle className="w-4 h-4 mr-2" /> Confirm Cancellation</>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrdersPage;
